import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import { useLocationContext } from "../../../contexts/LocationContext";

const AddEmployeeModal = ({
  isOpen,
  onClose,
  onSubmit,
  jobRoles = [],
  loading = false,
}) => {
  const { locations, currentLocation } = useLocationContext();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    jobTitle: "",
    department: "",
    locationId: currentLocation?.id || "",
    primaryJobRoleId: "",
    contractedHoursPerWeek: "",
    hourlyRate: "",
    payrollNumber: "",
    isEligibleForTronc: true,
    startDate: new Date().toISOString().split("T")[0],
  });

  const [errors, setErrors] = useState({});

  // Update locationId when currentLocation changes
  useEffect(() => {
    if (currentLocation?.id && !formData.locationId) {
      setFormData((prev) => ({
        ...prev,
        locationId: currentLocation.id,
      }));
    }
  }, [currentLocation?.id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If changing job role, auto-populate department
    if (name === "primaryJobRoleId") {
      const selectedRole = jobRoles.find((role) => role.id === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        department: selectedRole?.department || "", // Auto-populate department
        jobTitle: selectedRole?.name || prev.jobTitle, // Optional: also set job title
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error when field is modified
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.locationId) newErrors.locationId = "Location is required";
    if (!formData.primaryJobRoleId) newErrors.primaryJobRoleId = "Job role is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      ...formData,
      locationId: formData.locationId || null,
      contractedHoursPerWeek: formData.contractedHoursPerWeek
        ? parseFloat(formData.contractedHoursPerWeek)
        : null,
      hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : null,
      primaryJobRoleId: formData.primaryJobRoleId || null,
      startDate: formData.startDate
        ? new Date(formData.startDate).toISOString()
        : null,
    };

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      jobTitle: "",
      department: "",
      locationId: currentLocation?.id || "",
      primaryJobRoleId: "",
      contractedHoursPerWeek: "",
      hourlyRate: "",
      payrollNumber: "",
      isEligibleForTronc: true,
      startDate: new Date().toISOString().split("T")[0],
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[1200]"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[1201] flex items-center justify-center p-4">
        <div
          className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-heading font-semibold">
                Add New Employee
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Enter the employee details below
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-muted rounded-md transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="label">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`input ${errors.firstName ? "border-error" : ""}`}
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="text-sm text-error mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="label">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`input ${errors.lastName ? "border-error" : ""}`}
                  placeholder="Smith"
                />
                {errors.lastName && (
                  <p className="text-sm text-error mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input ${errors.email ? "border-error" : ""}`}
                  placeholder="john.smith@example.com"
                />
                {errors.email && (
                  <p className="text-sm text-error mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="label">Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="input"
                  placeholder="07700 900000"
                />
              </div>

              {/* Location - Now a dropdown */}
              <div>
                <label className="label">Location *</label>
                <select
                  name="locationId"
                  value={formData.locationId}
                  onChange={handleChange}
                  className={`input ${errors.locationId ? "border-error" : ""}`}
                >
                  <option value="">Select a location...</option>
                  {locations?.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                {errors.locationId && (
                  <p className="text-sm text-error mt-1">{errors.locationId}</p>
                )}
                {locations && locations.length === 0 && (
                  <p className="text-sm text-warning mt-1">
                    No locations available. Please create a location first.
                  </p>
                )}
              </div>

              {/* Job Title */}
              <div>
                <label className="label">Job Title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleChange}
                  className="input"
                  placeholder="Waiter"
                />
              </div>

              {/* Job Role - Now Required */}
              <div>
                <label className="label">Job Role *</label>
                <select
                  name="primaryJobRoleId"
                  value={formData.primaryJobRoleId}
                  onChange={handleChange}
                  className={`input ${errors.primaryJobRoleId ? "border-error" : ""}`}
                  required
                >
                  <option value="">Select a role...</option>
                  {jobRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.primaryJobRoleId && (
                  <p className="text-sm text-error mt-1">{errors.primaryJobRoleId}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Selecting a role will auto-populate the department
                </p>
              </div>

              {/* Department - Auto-populated, read-only */}
              <div>
                <label className="label">Department</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  readOnly
                  disabled
                  className="input bg-muted cursor-not-allowed"
                  placeholder="Auto-populated from role"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Automatically set based on selected job role
                </p>
              </div>

              {/* Hours per Week */}
              <div>
                <label className="label">Contracted Hours/Week</label>
                <input
                  type="number"
                  name="contractedHoursPerWeek"
                  value={formData.contractedHoursPerWeek}
                  onChange={handleChange}
                  className="input"
                  placeholder="40"
                  min="0"
                  max="168"
                  step="0.5"
                />
              </div>

              {/* Hourly Rate */}
              <div>
                <label className="label">Hourly Rate (�)</label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  className="input"
                  placeholder="12.50"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Payroll Number */}
              <div>
                <label className="label">Payroll Number</label>
                <input
                  type="text"
                  name="payrollNumber"
                  value={formData.payrollNumber}
                  onChange={handleChange}
                  className="input"
                  placeholder="EMP001"
                />
              </div>

              {/* Start Date */}
              <div>
                <label className="label">Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={`input ${errors.startDate ? "border-error" : ""}`}
                />
                {errors.startDate && (
                  <p className="text-sm text-error mt-1">{errors.startDate}</p>
                )}
              </div>
            </div>

            {/* TRONC Eligibility */}
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isEligibleForTronc"
                  checked={formData.isEligibleForTronc}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-border text-primary focus:ring-ring"
                />
                <div>
                  <span className="font-medium">
                    Eligible for TRONC Distribution
                  </span>
                  <p className="text-sm text-muted-foreground">
                    This employee will be included in tip distributions
                  </p>
                </div>
              </label>
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/50">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner spinner-sm" />
                  Adding...
                </>
              ) : (
                <>
                  <Icon name="UserPlus" size={18} />
                  Add Employee
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEmployeeModal;
